import { useEffect, useState } from 'react';
import { findByValue } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { currentDate, getDueInDate } from '@witmetrics/utils/time';
import type { DecoratedTaskTemplate } from '@witmetrics/utils';
import {
  useActivePractice,
  useCurrentUser,
  useDispatch,
  usePracticeTaskTemplates,
} from '@witmetrics/data-store';
import { buildUserOption } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useDate } from '@/hooks/useDate';
import { useString } from '@/hooks/useString';
import { useToggle } from '@/hooks/useToggle';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import type { PendingTaskData } from '@/providers/NewCaseProvider';
import Button from '@/components/Button';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import DateTimeInput, {
  DEADLINE_PRESETS,
} from '@/components/inputs/DateTimeInput';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from '@/components/inputs/SearchableDropdown';
import TextField from '@/components/inputs/TextField';
import TaskTemplateSearchDialog from '@/dialogs/TaskTemplateSearchDialog';
import { StickyNoteIcon } from '@/icons';
import {
  classes,
  fetchPracticeUsers,
  fetchTaskTemplates,
  getInitialDueAt,
  searchAssignedTo,
  validateFields,
} from './utils';

export type PendingTaskDialogProps = {
  initialData?: PendingTaskData;
  onSave: (data: PendingTaskData) => void;
  onClose: () => void;
};

export default function PendingTaskDialog({
  initialData,
  onSave,
  onClose,
}: PendingTaskDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const activePractice = useActivePractice();
  const taskTemplates = usePracticeTaskTemplates(activePractice?.id);
  const [practiceUsers, setPracticeUsers] = useState([
    buildUserOption(currentUser!),
  ]);
  const [assignedTo, setAssignedTo] =
    useState<SearchableDropdownOption<number> | null>(
      buildUserOption(currentUser!)
    );
  const [dueAt, setDueAt] = useDate(
    initialData?.dueAt ? new Date(initialData.dueAt) : getInitialDueAt()
  );
  const [description, setDescription] = useString(
    initialData?.description ?? ''
  );
  const { setInvalidFields } = useInvalidFields();
  const [isTemplateSearchOpen, toggleTemplateSearch] = useToggle();

  useEffect(() => {
    if (activePractice) {
      handleFetchPracticeUsers();
      handleFetchTaskTemplates();
    }
  }, [activePractice]);

  useEffect(() => {
    if (!initialData) return;
    const match = findByValue(practiceUsers, initialData.assignedTo);
    if (match) setAssignedTo(match);
  }, [practiceUsers, initialData]);

  const handleFetchPracticeUsers = async () => {
    try {
      const users = await fetchPracticeUsers(activePractice!.id);
      setPracticeUsers(users);
    } catch (err) {
      onApiError(err, 'Error fetching practice users', () =>
        handleFetchPracticeUsers()
      );
    }
  };

  const handleFetchTaskTemplates = async () => {
    try {
      const templates = await fetchTaskTemplates(activePractice!.id);
      dispatch.addTaskTemplates(templates);
    } catch (err) {
      onApiError(err, 'Error fetching task templates', () =>
        handleFetchTaskTemplates()
      );
    }
  };

  const handleSelectTemplate = (template: DecoratedTaskTemplate) => {
    setDescription(template.description);
    if (template.assignedTo) {
      const match = findByValue(practiceUsers, template.assignedTo.id);
      if (match) setAssignedTo(match);
    }
    if (template.dueIn) {
      setDueAt(getDueInDate(currentDate(), template.dueIn));
    }
  };

  const handleSearchAssignedTo = async (searchQuery: string) => {
    return searchAssignedTo(searchQuery, practiceUsers);
  };

  const handleSave = () => {
    const invalid = validateFields({ dueAt, assignedTo, description });
    if (!isEmpty(invalid)) return setInvalidFields(invalid);
    onSave({
      dueAt: dueAt!,
      assignedTo: assignedTo!.value,
      description: description.trim(),
    });
    onClose();
  };

  const isSaveDisabled = () => {
    return !isEmpty(validateFields({ dueAt, assignedTo, description }));
  };

  const canUseTemplate = !initialData && !isEmpty(taskTemplates);

  return (
    <>
      {isTemplateSearchOpen && (
        <TaskTemplateSearchDialog
          onSelect={handleSelectTemplate}
          onClose={() => toggleTemplateSearch(false)}
        />
      )}
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle
          title={initialData ? 'Edit Task' : 'New Task'}
          actions={
            canUseTemplate && (
              <Button
                size="sm"
                icon={<StickyNoteIcon />}
                onClick={() => toggleTemplateSearch(true)}>
                Templates
              </Button>
            )
          }
          onClose={onClose}
        />
        <DialogBody className={classes.body}>
          <DateTimeInput
            disablePast={false}
            className={classes.input}
            label="Deadline"
            value={dueAt}
            presets={DEADLINE_PRESETS}
            onApply={() => null}
            onChange={setDueAt}
          />
          <SearchableDropdown
            className={classes.input}
            label="Assigned To"
            value={assignedTo}
            initialOptions={practiceUsers}
            onSearch={handleSearchAssignedTo}
            onSelect={setAssignedTo}
          />
          <TextField
            multiline
            className={classes.input}
            minRows={3}
            maxRows={5}
            label="Description"
            value={description}
            onChange={setDescription}
          />
        </DialogBody>
        <DefaultDialogActions
          disabled={isSaveDisabled()}
          confirmLabel="Save"
          onCancel={onClose}
          onConfirm={handleSave}
        />
      </Dialog>
    </>
  );
}
